import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import ClientPicker from "@/components/ClientPicker";
import { Copy, KeyRound, Plus, RotateCcw, Trash2, X, Check, Webhook, Code2 } from "lucide-react";
import { toast } from "sonner";
import { getBackendUrl as resolveBackendUrl } from "@/lib/runtimeConfig";

function NewKeyModal({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [clientName, setClientName] = useState("");
    const [clientId, setClientId] = useState("");
    const [rpm, setRpm] = useState("");
    const [rpd, setRpd] = useState("");
    const [monthly, setMonthly] = useState("");
    const [monthlyBudget, setMonthlyBudget] = useState("");
    const [maxDocs, setMaxDocs] = useState("");
    const [maxSize, setMaxSize] = useState("");
    const [expires, setExpires] = useState("");
    const [loading, setLoading] = useState(false);
    const [plaintext, setPlaintext] = useState("");

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const body = {
                name: name || "Unnamed key",
                client_name: clientName || null,
                client_id: clientId || null,
                rate_limit_per_minute: rpm ? Number(rpm) : null,
                rate_limit_per_day: rpd ? Number(rpd) : null,
                monthly_limit: monthly ? Number(monthly) : null,
                monthly_cost_budget_usd: monthlyBudget ? Number(monthlyBudget) : null,
                max_documents_per_request: maxDocs ? Number(maxDocs) : null,
                max_file_size_mb: maxSize ? Number(maxSize) : null,
                expires_at: expires ? new Date(expires).toISOString() : null,
            };
            const { data } = await api.post("/admin/keys", body);
            setPlaintext(data.plaintext_key);
            onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create key");
        } finally {
            setLoading(false);
        }
    };

    const copy = async () => {
        await navigator.clipboard.writeText(plaintext);
        toast.success("API key copied");
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
            <div
                className="w-full max-w-md bg-[#0a0a0a] border border-white/15 rounded-md"
                data-testid="create-key-modal"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                        <div className="font-display text-lg tracking-tight">
                            {plaintext ? "API key created" : "Create API key"}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            onClose();
                            setTimeout(() => setPlaintext(""), 250);
                        }}
                        data-testid="create-key-close"
                        className="text-white/60 hover:text-white"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {plaintext ? (
                    <div className="p-5 space-y-4">
                        <div className="text-xs text-amber-400 border border-amber-400/30 bg-amber-400/5 rounded-md px-3 py-2">
                            This is the only time the plaintext key will be shown. Copy and
                            store it securely.
                        </div>
                        <div className="bg-black border border-white/10 rounded-md p-3 font-mono text-xs break-all" data-testid="plaintext-key">
                            {plaintext}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={copy}
                                data-testid="copy-key-button"
                                className="flex-1 bg-white text-black py-2 rounded-md text-sm font-medium hover:bg-white/90 transition-colors flex items-center justify-center gap-2"
                            >
                                <Copy className="w-3.5 h-3.5" strokeWidth={1.8} /> Copy key
                            </button>
                            <button
                                onClick={() => {
                                    onClose();
                                    setTimeout(() => setPlaintext(""), 250);
                                }}
                                className="px-4 py-2 border border-white/15 rounded-md text-sm hover:bg-white/5"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="p-5 space-y-3">
                        <Field label="Key name" required value={name} onChange={setName} placeholder="Internal ETL · staging" testId="key-name" />
                        <Field label="Client / application" value={clientName} onChange={setClientName} placeholder="Sponsor-A CRO" testId="key-client" />
                        <ClientPicker
                            label="Billing client"
                            value={clientId}
                            onChange={setClientId}
                            testId="key-client-id"
                        />
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Requests / min" type="number" value={rpm} onChange={setRpm} placeholder="20" testId="key-rpm" />
                            <Field label="Requests / day" type="number" value={rpd} onChange={setRpd} placeholder="500" testId="key-rpd" />
                            <Field label="Monthly limit" type="number" value={monthly} onChange={setMonthly} placeholder="10000" testId="key-monthly" />
                            <Field label="Monthly budget USD" type="number" value={monthlyBudget} onChange={setMonthlyBudget} placeholder="100" testId="key-monthly-budget" />
                            <Field label="Max docs / req" type="number" value={maxDocs} onChange={setMaxDocs} placeholder="10" testId="key-maxdocs" />
                            <Field label="Max file size MB" type="number" value={maxSize} onChange={setMaxSize} placeholder="30" testId="key-maxsize" />
                            <Field label="Expires at" type="datetime-local" value={expires} onChange={setExpires} testId="key-expires" />
                        </div>
                        <button
                            disabled={loading}
                            type="submit"
                            data-testid="create-key-submit"
                            className="w-full bg-white text-black py-2.5 rounded-md text-sm font-medium hover:bg-white/90 disabled:opacity-60 transition-all"
                        >
                            {loading ? "Creating…" : "Create key"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", placeholder, required, testId }) {
    return (
        <label className="block">
            <span className="label-xs block mb-1.5">{label}</span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 focus:ring-2 focus:ring-white/10"
            />
        </label>
    );
}

export default function ApiKeysPage() {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [rotateId, setRotateId] = useState(null);
    const [rotatePlain, setRotatePlain] = useState("");
    const [webhookSecret, setWebhookSecret] = useState(null);

    const [rotateConfirm, setRotateConfirm] = useState(false);
    const [snippetKey, setSnippetKey] = useState(null);
    const showWebhookSecret = async (k) => {
        try {
            const { data } = await api.get(`/admin/keys/${k.id}/webhook-secret`);
            setWebhookSecret({ key: k, ...data });
            setRotateConfirm(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    const rotateWebhookSecret = async (keyId) => {
        if (!rotateConfirm) {
            setRotateConfirm(true);
            return;
        }
        try {
            const { data } = await api.post(`/admin/keys/${keyId}/webhook-secret/rotate`);
            // Force a fresh object so React treats this as a new render of the modal contents.
            setWebhookSecret((cur) => {
                if (!cur || cur.key.id !== keyId) return cur;
                return { ...cur, webhook_signing_secret: data.webhook_signing_secret, _rotatedAt: Date.now() };
            });
            setRotateConfirm(false);
            toast.success("Rotated. Update your receiver with the new secret.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rotate failed");
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/keys");
            setKeys(data.items || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const rotate = async (id) => {
        try {
            const { data } = await api.post(`/admin/keys/${id}/rotate`);
            setRotateId(id);
            setRotatePlain(data.plaintext_key);
            toast.success("Key rotated. Copy the new plaintext key now.");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rotate failed");
        }
    };

    const revoke = async (id) => {
        if (!confirm("Revoke this API key? External clients will immediately lose access.")) return;
        try {
            await api.delete(`/admin/keys/${id}`);
            toast.success("Key revoked");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Revoke failed");
        }
    };

    const toggleStatus = async (k) => {
        const next = k.status === "active" ? "inactive" : "active";
        try {
            await api.patch(`/admin/keys/${k.id}`, { status: next });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    return (
        <div className="px-8 py-8">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Access Control</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">API Keys</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-xl">
                        Provision, rotate, and revoke API keys per client. Keys are hashed
                        at rest and the plaintext value is shown only once.
                    </p>
                </div>
                <button
                    onClick={() => setOpen(true)}
                    data-testid="create-key-button"
                    className="bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-white/90 flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" strokeWidth={1.8} /> New key
                </button>
            </div>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3">Name</th>
                            <th className="label-xs px-5 py-3">Client</th>
                            <th className="label-xs px-5 py-3">Billing ID</th>
                            <th className="label-xs px-5 py-3">Key</th>
                            <th className="label-xs px-5 py-3">Status</th>
                            <th className="label-xs px-5 py-3">Usage</th>
                            <th className="label-xs px-5 py-3">Monthly budget</th>
                            <th className="label-xs px-5 py-3">Last used</th>
                            <th className="label-xs px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={9} className="px-5 py-8 text-center text-xs text-white/40 font-mono">
                                    Loading keys…
                                </td>
                            </tr>
                        ) : keys.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-5 py-16 text-center">
                                    <KeyRound className="w-10 h-10 text-white/20 mx-auto mb-3" strokeWidth={1.2} />
                                    <div className="text-sm text-white/60">No API keys yet</div>
                                    <div className="text-xs text-white/40 mt-1 font-mono">
                                        Create one to start calling /api/v1/forms/generate
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            keys.map((k) => (
                                <tr key={k.id} className="data-row border-t border-white/5" data-testid={`key-row-${k.id}`}>
                                    <td className="px-5 py-3">
                                        <div className="text-sm">{k.name}</div>
                                        {k.notes ? <div className="text-[11px] text-white/40 font-mono mt-0.5">{k.notes}</div> : null}
                                    </td>
                                    <td className="px-5 py-3 text-sm">{k.client_name || <span className="text-white/30">—</span>}</td>
                                    <td className="px-5 py-3 font-mono text-xs" data-testid={`key-client-id-${k.id}`}>
                                        {k.client_id ? (
                                            <span className="px-1.5 py-0.5 rounded-sm border border-[#7C3AED]/30 bg-[#7C3AED]/5 text-[#7C3AED]">
                                                {k.client_id}
                                            </span>
                                        ) : (
                                            <span className="text-white/30">—</span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs text-white/80">{k.masked_key}</td>
                                    <td className="px-5 py-3">
                                        <button onClick={() => toggleStatus(k)} title="Toggle active/inactive">
                                            <StatusBadge status={k.status} />
                                        </button>
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs">
                                        {k.usage_count ?? 0}
                                        <span className="text-white/30"> calls</span>
                                    </td>
                                    <td className="px-5 py-3">
                                        <BudgetCell spent={k.mtd_cost_usd || 0} budget={k.monthly_cost_budget_usd} keyId={k.id} />
                                    </td>
                                    <td className="px-5 py-3 font-mono text-xs text-white/60">
                                        {k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}
                                    </td>
                                    <td className="px-5 py-3">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <button
                                                onClick={() => showWebhookSecret(k)}
                                                data-testid={`webhook-secret-${k.id}`}
                                                title="Webhook signing secret"
                                                className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
                                            >
                                                <Webhook className="w-3.5 h-3.5" strokeWidth={1.6} />
                                            </button>
                                            <button
                                                onClick={() => setSnippetKey(k)}
                                                data-testid={`get-forms-snippet-${k.id}`}
                                                title="Get Forms code snippet (curl / Python / Java)"
                                                className="p-1.5 rounded hover:bg-violet-400/15 text-white/70 hover:text-violet-300"
                                            >
                                                <Code2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                            </button>
                                            <button
                                                onClick={() => rotate(k.id)}
                                                data-testid={`rotate-${k.id}`}
                                                title="Rotate key"
                                                className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} />
                                            </button>
                                            <button
                                                onClick={() => revoke(k.id)}
                                                data-testid={`revoke-${k.id}`}
                                                title="Revoke"
                                                className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <NewKeyModal open={open} onClose={() => setOpen(false)} onCreated={load} />

            {webhookSecret && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => { setWebhookSecret(null); setRotateConfirm(false); }}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="webhook-secret-modal">
                        <div className="px-5 py-4 border-b border-white/10 flex items-center">
                            <Webhook className="w-4 h-4 text-[#7C3AED] mr-2" strokeWidth={1.8} />
                            <div className="font-display text-lg tracking-tight">Webhook signing secret</div>
                            <div className="ml-3 text-[10px] font-mono text-white/40">{webhookSecret.key.name}</div>
                            <button onClick={() => { setWebhookSecret(null); setRotateConfirm(false); }} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="text-xs text-amber-400 border border-amber-400/30 bg-amber-400/5 rounded-md px-3 py-2">
                                Treat this like a password. Receivers use it to verify HMAC-SHA256 signatures on incoming webhooks.
                            </div>
                            <div>
                                <div className="label-xs mb-1.5">Secret</div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono break-all" data-testid="webhook-secret-value">
                                        {webhookSecret.webhook_signing_secret}
                                    </code>
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(webhookSecret.webhook_signing_secret); toast.success("Copied"); }}
                                        className="p-2 border border-white/15 rounded-md hover:bg-white/5"
                                    >
                                        <Copy className="w-3.5 h-3.5" strokeWidth={1.8} />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div className="label-xs mb-1.5">Header format</div>
                                <code className="block bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-[11px] font-mono text-white/70">
                                    {webhookSecret.header_format}
                                </code>
                                <div className="text-[10px] text-white/40 mt-1.5 font-mono">
                                    {webhookSecret.verification_hint}
                                </div>
                            </div>
                            <div className="pt-2 border-t border-white/10 flex justify-end gap-2">
                                {rotateConfirm && (
                                    <button
                                        onClick={() => setRotateConfirm(false)}
                                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={() => rotateWebhookSecret(webhookSecret.key.id)}
                                    data-testid="webhook-secret-rotate"
                                    className={`px-3 py-2 border rounded-md text-xs font-medium flex items-center gap-1.5 ${
                                        rotateConfirm
                                            ? "border-red-400/50 text-red-400 bg-red-400/10 hover:bg-red-400/20"
                                            : "border-amber-400/40 text-amber-400 hover:bg-amber-400/10"
                                    }`}
                                >
                                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} />
                                    {rotateConfirm ? "Click again to confirm rotate" : "Rotate secret"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {rotateId && rotatePlain && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in">
                    <div className="w-full max-w-md bg-[#0a0a0a] border border-white/15 rounded-md p-5 space-y-4">
                        <div className="flex items-center gap-2">
                            <RotateCcw className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                            <div className="font-display text-lg tracking-tight">Key rotated</div>
                        </div>
                        <div className="text-xs text-amber-400 border border-amber-400/30 bg-amber-400/5 rounded-md px-3 py-2">
                            The old key is invalidated. Copy the new plaintext key now.
                        </div>
                        <div className="bg-black border border-white/10 rounded-md p-3 font-mono text-xs break-all" data-testid="rotated-key">
                            {rotatePlain}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={async () => {
                                    await navigator.clipboard.writeText(rotatePlain);
                                    toast.success("Copied");
                                }}
                                className="flex-1 bg-white text-black py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2"
                            >
                                <Check className="w-3.5 h-3.5" /> Copy
                            </button>
                            <button
                                onClick={() => {
                                    setRotateId(null);
                                    setRotatePlain("");
                                }}
                                className="px-4 py-2 border border-white/15 rounded-md text-sm hover:bg-white/5"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {snippetKey && (
                <GetFormsSnippetModal
                    apiKey={snippetKey}
                    onClose={() => setSnippetKey(null)}
                />
            )}
        </div>
    );
}

// ============================================================
//  Get-Forms snippet modal — per-tenant code samples for the
//  versioned-package read endpoint (`GET /api/v1/forms/package/*`).
//  Pre-populates `client_id` from the row + suggests a sample
//  `study_id` from the tenant's most recent protocol (best-effort).
// ============================================================
function GetFormsSnippetModal({ apiKey, onClose }) {
    const [studyId, setStudyId] = useState("");
    const [lang, setLang] = useState("curl");
    const baseUrl = resolveBackendUrl();

    useEffect(() => {
        // Best-effort: fetch the most-recent protocol for this tenant so
        // the snippet has a real study_id to copy-paste.
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/protocols?limit=5${apiKey.client_id ? `&company_id=${encodeURIComponent(apiKey.client_id)}` : ""}`,
                );
                const proto = (data.items || []).find((p) => p.protocol_number);
                if (proto?.protocol_number) setStudyId(proto.protocol_number);
                else setStudyId("YOUR-STUDY-ID");
            } catch {
                setStudyId("YOUR-STUDY-ID");
            }
        })();
    }, [apiKey.client_id]);

    const clientId = apiKey.client_id || "YOUR_CLIENT_ID";
    const tokenPlaceholder = "<YOUR_BEARER_TOKEN>";
    const latestUrl = `${baseUrl}/api/v1/forms/package/latest?client_id=${encodeURIComponent(clientId)}&study_id=${encodeURIComponent(studyId || "YOUR-STUDY-ID")}`;
    const byVersionUrl = `${baseUrl}/api/v1/forms/package/by-version?client_id=${encodeURIComponent(clientId)}&study_id=${encodeURIComponent(studyId || "YOUR-STUDY-ID")}&package_version=v1`;
    const visitFilterUrl = `${latestUrl}&visit=Screening`;

    const snippets = {
        curl: `# Latest current package
curl -sS '${latestUrl}' \\
  -H 'Authorization: Bearer ${tokenPlaceholder}' \\
  -H 'Accept: application/json'

# Filter to a single visit
curl -sS '${visitFilterUrl}' \\
  -H 'Authorization: Bearer ${tokenPlaceholder}'

# Specific version
curl -sS '${byVersionUrl}' \\
  -H 'Authorization: Bearer ${tokenPlaceholder}'`,

        python: `import os, requests

BASE = "${baseUrl}"
TOKEN = os.environ["SITECENTRIC_TOKEN"]   # superdomain API key or admin JWT
CLIENT_ID = "${clientId}"
STUDY_ID  = "${studyId || "YOUR-STUDY-ID"}"

# 1) Latest current package
r = requests.get(
    f"{BASE}/api/v1/forms/package/latest",
    params={"client_id": CLIENT_ID, "study_id": STUDY_ID},
    headers={"Authorization": f"Bearer {TOKEN}"},
    timeout=30,
)
r.raise_for_status()
pkg = r.json()
print(pkg["generatedFormPackageVersion"], "->", pkg["visit_count"], "visits")

# 2) Single visit
r = requests.get(
    f"{BASE}/api/v1/forms/package/latest",
    params={"client_id": CLIENT_ID, "study_id": STUDY_ID, "visit": "Screening"},
    headers={"Authorization": f"Bearer {TOKEN}"},
)
visits = r.json().get("visits", [])
for v in visits:
    for f in v.get("forms", []):
        print(v["visitName"], "->", f["formName"], len(f.get("fields", [])), "fields")`,

        java: `// Requires aiservice-spring-boot-starter v1.1.0+ on the classpath.
// Add to pom.xml / build.gradle: us.sitecentric.aiservice:aiservice-spring-boot-starter
import us.sitecentric.aiservice.AiServiceClient;
import us.sitecentric.aiservice.forms.FormsApi;
import us.sitecentric.aiservice.forms.GeneratedFormPackage;

AiServiceClient client = AiServiceClient.builder()
    .baseUrl("${baseUrl}")
    .bearerToken(System.getenv("SITECENTRIC_TOKEN"))
    .build();

FormsApi forms = client.forms();

// Latest current package for this tenant + study
GeneratedFormPackage pkg = forms.getLatest()
    .clientId("${clientId}")
    .studyId("${studyId || "YOUR-STUDY-ID"}")
    .execute();
System.out.println(pkg.generatedFormPackageVersion() + " -> " + pkg.visitCount() + " visits");

// Filter to a single visit
GeneratedFormPackage screening = forms.getLatest()
    .clientId("${clientId}")
    .studyId("${studyId || "YOUR-STUDY-ID"}")
    .visit("Screening")
    .execute();`,
    };

    const copy = () => {
        navigator.clipboard.writeText(snippets[lang]);
        toast.success(`${lang} snippet copied`);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="get-forms-snippet-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-3xl bg-[#0a0a0a] border border-white/15 rounded-md"
            >
                <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
                    <Code2 className="w-4 h-4 text-violet-400" strokeWidth={1.8} />
                    <div className="font-display text-lg tracking-tight">Get Forms — code snippets</div>
                    <span className="text-[11px] font-mono text-white/40 ml-2">{apiKey.name}</span>
                    <span className="text-[11px] font-mono text-violet-300">{apiKey.client_id || "(no client)"}</span>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="text-xs text-white/60">
                        Tenant-scoped read of versioned form packages. Auth: either your admin <span className="font-mono">Bearer JWT</span> or a <span className="font-mono">superdomain</span> API key. Tenant API keys are not yet accepted on this endpoint — coming next iteration.
                    </div>
                    <div className="flex items-center gap-2">
                        {["curl", "python", "java"].map((l) => (
                            <button
                                key={l}
                                onClick={() => setLang(l)}
                                data-testid={`snippet-tab-${l}`}
                                className={`px-3 py-1.5 rounded text-xs font-mono ${lang === l ? "bg-violet-400/15 text-violet-300 border border-violet-400/40" : "text-white/50 border border-white/10 hover:text-white"}`}
                            >
                                {l}
                            </button>
                        ))}
                        <button
                            onClick={copy}
                            data-testid="snippet-copy-btn"
                            className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded border border-white/15 text-white/70 hover:text-white text-xs"
                        >
                            <Copy className="w-3 h-3" strokeWidth={1.75} />Copy
                        </button>
                    </div>
                    <pre
                        className="bg-[#050505] border border-white/10 rounded-md px-3 py-3 text-[11px] font-mono whitespace-pre-wrap text-white/80 max-h-[420px] overflow-y-auto"
                        data-testid="snippet-code-block"
                    >
                        {snippets[lang]}
                    </pre>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                        <div className="p-2 rounded border border-white/10 bg-white/[0.02]">
                            <div className="text-white/40 uppercase tracking-wide text-[10px] mb-0.5">Pre-populated</div>
                            <div><span className="text-white/40">client_id:</span> <code className="text-violet-300">{clientId}</code></div>
                            <div><span className="text-white/40">study_id:</span> <code className="text-violet-300">{studyId || "YOUR-STUDY-ID"}</code></div>
                        </div>
                        <div className="p-2 rounded border border-amber-400/15 bg-amber-400/5">
                            <div className="text-amber-300 uppercase tracking-wide text-[10px] mb-0.5">Datalake billing</div>
                            <div className="text-amber-300/80">Every retrieval records a metered <code>reuse_charge</code> against this tenant (source=datalake).</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BudgetCell({ spent, budget, keyId }) {
    const spentSafe = Number(spent) || 0;
    if (!budget) {
        return (
            <div className="min-w-[140px]" data-testid={`budget-cell-${keyId}`}>
                <div className="text-[11px] font-mono text-white/60">
                    ${spentSafe.toFixed(2)} MTD
                </div>
                <div className="text-[10px] text-white/30 font-mono">no budget set</div>
            </div>
        );
    }
    const budgetN = Number(budget) || 0;
    const pct = budgetN > 0 ? Math.min(100, (spentSafe / budgetN) * 100) : 0;
    const over = spentSafe >= budgetN && budgetN > 0;
    const nearly = !over && pct >= 80;
    const color = over ? "bg-red-400" : nearly ? "bg-amber-400" : "bg-[#7C3AED]";
    return (
        <div className="min-w-[160px]" data-testid={`budget-cell-${keyId}`}>
            <div className="flex items-center justify-between text-[11px] font-mono">
                <span className={over ? "text-red-400" : nearly ? "text-amber-400" : "text-white/80"}>
                    ${spentSafe.toFixed(2)}
                </span>
                <span className="text-white/40">/ ${budgetN.toFixed(0)}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                    data-testid={`budget-bar-${keyId}`}
                    className={`h-full ${color} transition-all`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="text-[10px] text-white/40 mt-0.5 font-mono">
                {over ? "budget exhausted" : `${pct.toFixed(0)}% used`}
            </div>
        </div>
    );
}

